/**
 * CashBookScreen — money in vs money out, day by day, over any range. Makes a
 * Day Close auditable after the fact.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadCashBook } from '@/reports/cashbook/cashBookDataSource';
import { buildCashBook, type CashBook } from '@/reports/cashbook/cashBook';

// ─── Component ───────────────────────────────────────────────────────────────

export function CashBookScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<CashBook | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const source = await loadCashBook(activeStoreId, from, to);
      setData(buildCashBook(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.days, [
        { header: 'Date', value: (r) => r.date },
        { header: 'In', value: (r) => r.inflow },
        { header: 'Out', value: (r) => r.outflow },
        { header: 'Net', value: (r) => r.net },
        { header: 'Cumulative', value: (r) => r.cumulative },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `CashBook_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildCashBookHtml(data, from, to),
        defaultFileName: `CashBook_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  const maxAbs = data ? Math.max(1, ...data.days.map((d) => Math.abs(d.net))) : 1;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.days.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.days.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.days.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">
          No money movements in this period.
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <KPI label="Money in" value={formatCurrency(data.totalIn)} />
            <KPI label="Money out" value={formatCurrency(data.totalOut)} warn={data.totalOut > 0} />
            <KPI label="Net cash flow" value={formatCurrency(data.net)} accent={data.net >= 0} warn={data.net < 0} />
          </div>

          {/* Breakdowns */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Card title="Money in by method">
              {data.receiptsByMethod.length === 0 ? (
                <div className="py-1 text-body text-fg-tertiary">No receipts.</div>
              ) : (
                data.receiptsByMethod.map((m) => (
                  <Row key={m.method} label={methodLabel(m.method)} amount={m.amount} />
                ))
              )}
            </Card>
            <Card title="Money out by source">
              <Row label="Supplier payments" amount={data.supplierPaymentTotal} warn />
              <Row label="Expenses" amount={data.expenseTotal} warn />
              {data.refundTotal > 0 && <Row label="Customer refunds" amount={data.refundTotal} warn />}
            </Card>
          </div>

          {/* Daily table */}
          <Card title="Daily cash flow">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Date</th>
                    <th className="pb-1 text-right font-normal">In</th>
                    <th className="pb-1 text-right font-normal">Out</th>
                    <th className="pb-1 text-right font-normal">Net</th>
                    <th className="pb-1 pl-4 text-left font-normal">Cumulative</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((day) => (
                    <tr key={day.date} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-primary tabular-nums">{day.date}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">
                        {day.inflow ? formatCurrency(day.inflow) : '–'}
                      </td>
                      <td className={cn('py-1.5 text-right tabular-nums', day.outflow ? 'text-warning-ink' : 'text-fg-tertiary/40')}>
                        {day.outflow ? formatCurrency(day.outflow) : '–'}
                      </td>
                      <td className={cn('py-1.5 text-right font-medium tabular-nums', day.net >= 0 ? 'text-fg-primary' : 'text-warning-ink')}>
                        {formatCurrency(day.net)}
                      </td>
                      <td className="py-1.5 pl-4">
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-1.5 rounded-full bg-accent/50"
                            style={{ width: `${Math.round((Math.abs(day.net) / maxAbs) * 60)}px` }} />
                          <span className="text-label tabular-nums text-fg-tertiary">{formatCurrency(day.cumulative)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stroke-hairline/20 font-medium">
                    <td className="pt-2 text-fg-primary">Total</td>
                    <td className="pt-2 text-right tabular-nums text-fg-primary">{formatCurrency(data.totalIn)}</td>
                    <td className="pt-2 text-right tabular-nums text-warning-ink">{formatCurrency(data.totalOut)}</td>
                    <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.net)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="mt-2 text-label text-fg-tertiary">Cumulative opens at ₹0 on the first day of the range — a period cash-flow trend, not a drawer balance.</p>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function methodLabel(m: string): string {
  if (m === 'OTHER') return 'Other';
  if (m === 'UPI') return 'UPI';
  return m.charAt(0) + m.slice(1).toLowerCase();
}

function KPI({ label, value, accent, warn }: { label: string; value: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums',
        warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, amount, warn }: { label: string; amount: number; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-body text-fg-secondary">{label}</span>
      <span className={cn('text-body tabular-nums', warn ? 'text-warning-ink' : 'text-fg-primary')}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildCashBookHtml(d: CashBook, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);

  const rows = d.days.map((day) =>
    `<tr><td>${day.date}</td>
     <td style="text-align:right">${day.inflow ? fmt(day.inflow) : '–'}</td>
     <td style="text-align:right">${day.outflow ? fmt(day.outflow) : '–'}</td>
     <td style="text-align:right">${fmt(day.net)}</td>
     <td style="text-align:right">${fmt(day.cumulative)}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}h2{font-size:13px;margin-top:20px;text-transform:uppercase;color:#666}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}th,td{padding:6px 8px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child{text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Cash Book</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · In ${fmt(d.totalIn)} · Out ${fmt(d.totalOut)} · Net ${fmt(d.net)}</p>
<h2>Money out by source</h2>
<table><tbody>
<tr><td>Supplier payments</td><td style="text-align:right">${fmt(d.supplierPaymentTotal)}</td></tr>
<tr><td>Expenses</td><td style="text-align:right">${fmt(d.expenseTotal)}</td></tr>
${d.refundTotal > 0 ? `<tr><td>Customer refunds</td><td style="text-align:right">${fmt(d.refundTotal)}</td></tr>` : ''}
</tbody></table>
<h2>Daily cash flow</h2>
<table>
<thead><tr><th>Date</th><th>In</th><th>Out</th><th>Net</th><th>Cumulative</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td>Total</td><td style="text-align:right">${fmt(d.totalIn)}</td><td style="text-align:right">${fmt(d.totalOut)}</td><td style="text-align:right">${fmt(d.net)}</td><td></td></tr></tfoot>
</table>
<p style="color:#999;font-size:11px">Cumulative opens at ₹0 on the first day — a period trend, not a drawer balance.</p>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
